import { useNavigate } from "react-router-dom";
import i18n from "../../../language/i18n";
import { BaseModal, PrimaryButton, SecondaryButton } from "../../../library";
import { useAuth } from "../../../context/UserContextProvider";
import { applyUrlParamUpdates } from "../../../utilization/UrlSelectionUtilization";
import { subjectDescriptionNavigationModalStyles } from "./SubjectDescriptionNavigationModalStyles";

/**
 * The confirm step between a weak outcome and /learning-path — "Konu Anlatımına Git".
 *
 * It lived inside `OutcomeGraphStrongWeak` while the güçlü/zayıf cards were its only entry point.
 * Genel durum now offers the same jump from its outcome rows, so the dialog moved out here instead of
 * being copied: one confirm text, one preview, one navigation contract for both tabs. (A third copy
 * still sits in `OutcomeGraph.tsx`, which nothing imports — that file is dead and deliberately left
 * alone rather than half-migrated.)
 *
 * Prefix: sdn-
 */

export interface ISubjectDescriptionNavigationTarget {
  code?: string | null;
  name?: string | null;
  // The score the learner IS LOOKING AT, already a percentage (0–100), or null when the row carries
  // no evidence. Passed in rather than derived: the two callers hold it in different shapes — the
  // cards keep raw `score`/`count`, the Genel durum tree keeps `aggregatedAveragePercentage` — and
  // the preview has to agree with the number on the row that was clicked, not recompute its own.
  scorePercent?: number | null;
}

/**
 * Resolves an outcome code to the konu anlatımı built on it. Codes are the join key on purpose: a
 * category id may NOT be requested from the server (the client already holds the tree), and the
 * report rows carry the code.
 */
export const useSubjectDescriptionLookup = () => {
  const { subjectDescriptionDTOList } = useAuth();
  const findSubjectDescriptionByOutcomeCode = (outcomeCode?: string | null) => {
    if (!subjectDescriptionDTOList || !outcomeCode) return null;
    return (
      subjectDescriptionDTOList.find(
        (subjectDescriptionDTO: any) =>
          subjectDescriptionDTO.categoryDTO.code === outcomeCode,
      ) || null
    );
  };
  return { findSubjectDescriptionByOutcomeCode };
};

interface ISubjectDescriptionNavigationModalProps {
  // `null` closes it. The target IS the open state, so a caller cannot leave a stale outcome behind a
  // closed dialog and then reopen it on the wrong outcome.
  target?: ISubjectDescriptionNavigationTarget | null;
  coterieType?: string;
  onClose: () => void;
}

const SubjectDescriptionNavigationModal = ({
  target,
  coterieType,
  onClose,
}: ISubjectDescriptionNavigationModalProps) => {
  const navigate = useNavigate();
  const { findSubjectDescriptionByOutcomeCode } = useSubjectDescriptionLookup();

  const scorePercent =
    target && typeof target.scorePercent === "number"
      ? target.scorePercent
      : null;
  // Same three tiers the report rows and cards colour by, so the preview reads as the row it came
  // from rather than as a second opinion on it.
  const scoreTier =
    scorePercent === null
      ? null
      : scorePercent < 34
        ? "low"
        : scorePercent < 67
          ? "mid"
          : "high";

  const confirmNavigate = () => {
    const matchedSubjectDescription = findSubjectDescriptionByOutcomeCode(
      target?.code,
    );
    // Re-resolved at confirm time, not trusted from the open: the list is context state and can be
    // replaced (profile/season change) while the dialog is up.
    if (!matchedSubjectDescription) return;
    onClose();
    // Handed over in the QUERY, not in `location.state`: state is dropped by a refresh and cannot be
    // copied out of the address bar, so the jump this modal makes was neither reload-survivable nor
    // shareable. /learning-path reads both from the URL. See UrlSelectionUtilization.
    navigate({
      pathname: "/learning-path",
      search: applyUrlParamUpdates("", {
        lesson: coterieType,
        topic: matchedSubjectDescription.id,
      }),
    });
  };

  return (
    <BaseModal
      title={i18n.t("subject_description_navigation_title")}
      size="small"
      icon="📖"
      isOpen={target !== null && target !== undefined}
      onAfterOpen={() => {}}
      onRequestClose={onClose}
    >
      <style>{subjectDescriptionNavigationModalStyles}</style>
      <div className="sdn-body">
        {target ? (
          <div className="sdn-outcome">
            <div className="sdn-outcome-main">
              <span className="sdn-outcome-label">{i18n.t("outcome")}</span>
              <p className="sdn-outcome-name">{target.name}</p>
            </div>
            {scorePercent !== null ? (
              <div className={`sdn-score sdn-score--${scoreTier}`}>
                <span className="sdn-score-value">
                  {scorePercent.toFixed(0)}%
                </span>
                <span className="sdn-score-label">{i18n.t("avg_score")}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <p className="sdn-text">
          {i18n.t("subject_description_navigation_confirmation_text")}
        </p>

        <div className="sdn-actions">
          <SecondaryButton
            title={i18n.t("cancel")}
            size="medium"
            iconPosition="left"
            onClick={onClose}
          />
          <PrimaryButton
            title={i18n.t("continue")}
            size="medium"
            iconPosition="right"
            onClick={confirmNavigate}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default SubjectDescriptionNavigationModal;
